import { useParams, Link } from 'react-router-dom'
import { useState } from 'react'
import { motion } from 'framer-motion'
import { ArrowLeft, ExternalLink, Database, Beaker } from 'lucide-react'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Skeleton } from '../components/ui/Skeleton'
import { useFujisanHits } from '../api/hooks'
import type { FujisanHit, CompoundInfo } from '../api/hooks'
import { getUniprotUrl } from '../utils/externalLinks'

// Get Rhea URL from ID (handles RHEA:12345 format)
function getRheaUrl(rheaId: string): string {
  const numericId = rheaId.replace(/^RHEA:/i, '')
  return `https://www.rhea-db.org/rhea/${numericId}`
}

// Parse formula into JSX with subscripts (e.g., C6H12O6 -> C<sub>6</sub>H<sub>12</sub>O<sub>6</sub>)
function FormulaDisplay({ formula }: { formula: string | undefined }) {
  if (!formula) return <span className="text-gray-500">-</span>

  const parts: React.ReactNode[] = []
  let i = 0
  let key = 0

  while (i < formula.length) {
    const char = formula[i]
    if (/\d/.test(char)) {
      // Collect all consecutive digits
      let num = ''
      while (i < formula.length && /\d/.test(formula[i])) {
        num += formula[i]
        i++
      }
      parts.push(<sub key={key++}>{num}</sub>)
    } else {
      parts.push(<span key={key++}>{char}</span>)
      i++
    }
  }

  return <>{parts}</>
}

export function FujisanHitsPage() {
  const { clusterId, proteinId } = useParams<{ clusterId: string; proteinId: string }>()
  const [activeTab, setActiveTab] = useState<'entries' | 'participants'>('entries')
  const [limit, setLimit] = useState(50)

  const { data, isLoading, error } = useFujisanHits(
    proteinId || '',
    clusterId || '',
    limit
  )

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-white mb-4">Failed to load FUJISAN hits</h2>
        <Link to={`/proteins/${clusterId}/${proteinId}`}>
          <Button variant="secondary" leftIcon={<ArrowLeft className="w-4 h-4" />}>
            Back to Protein
          </Button>
        </Link>
      </div>
    )
  }

  const hits = data.data || []
  const compounds = data.unique_compounds || []

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Link
        to={`/proteins/${clusterId}/${proteinId}`}
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to {proteinId}
      </Link>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold text-white">
          FUJISAN Function Predictions
        </h1>
        <p className="text-gray-400 mt-2">
          Structure-based function prediction for{' '}
          <span className="font-mono text-accent-cyan">{proteinId}</span>
          {' '}from cluster{' '}
          <Link to={`/clusters/${clusterId}`} className="font-mono text-accent-cyan hover:underline">
            {clusterId}
          </Link>
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          <Badge variant="green" size="md">
            {hits.length} SwissProt entries found
          </Badge>
          {compounds.length > 0 && (
            <Badge variant="cyan" size="md">
              {compounds.length} reaction participants
            </Badge>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          FUJISAN predicts protein function by comparing AlphaFold structures to experimentally characterized proteins.
        </p>
      </motion.div>

      {/* Limit selector */}
      <div className="flex items-center gap-4">
        <label className="text-sm text-gray-400">Show top:</label>
        <select
          value={limit}
          onChange={(e) => setLimit(parseInt(e.target.value, 10))}
          className="bg-dark-bg-secondary border border-white/10 rounded px-3 py-1 text-white text-sm"
        >
          <option value={50} style={{ backgroundColor: '#1a1a24', color: '#f3f4f6' }}>50</option>
          <option value={100} style={{ backgroundColor: '#1a1a24', color: '#f3f4f6' }}>100</option>
          <option value={200} style={{ backgroundColor: '#1a1a24', color: '#f3f4f6' }}>200</option>
          <option value={500} style={{ backgroundColor: '#1a1a24', color: '#f3f4f6' }}>500</option>
        </select>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        <button
          onClick={() => setActiveTab('entries')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === 'entries'
              ? 'bg-accent-green/20 text-accent-green border-b-2 border-accent-green'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Database className="w-4 h-4 inline mr-2" />
          SwissProt Entries ({hits.length})
        </button>
        <button
          onClick={() => setActiveTab('participants')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === 'participants'
              ? 'bg-accent-cyan/20 text-accent-cyan border-b-2 border-accent-cyan'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Beaker className="w-4 h-4 inline mr-2" />
          Reaction participants ({compounds.length})
        </button>
      </div>

      {/* Content */}
      {activeTab === 'entries' ? (
        <Card>
          <CardContent className="pt-6">
            {hits.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-14">Rank</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium">RecName</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-24">Accession</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-28">Rhea ID</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-16">Score</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">Bit score</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">Full seqID</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-24">Domain RMSD</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-24">Domain seqID</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">Domain TM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hits.map((hit: FujisanHit, idx: number) => {
                      const hasReactions = hit.reactions && hit.reactions.length > 0
                      const numRows = hasReactions ? hit.reactions.length + 1 : 1
                      const rowClass = idx % 2 === 0 ? 'bg-white/[0.02]' : ''
                      const rheaIdsList = hit.rhea_ids?.filter(id => id && id.startsWith('RHEA:')) || []

                      return (
                        <>
                          {/* Main row */}
                          <tr
                            key={`hit-${idx}`}
                            className={`border-b border-white/5 ${rowClass}`}
                          >
                            <td className="py-3 px-3 text-gray-500 align-top" rowSpan={numRows}>
                              {hit.rank}
                            </td>
                            <td className="py-3 px-3 text-gray-300" title={hit.recname}>
                              {hit.recname || '-'}
                            </td>
                            <td className="py-3 px-3">
                              <a
                                href={getUniprotUrl(hit.uniprot_id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-mono text-accent-green hover:underline inline-flex items-center gap-1"
                              >
                                {hit.uniprot_id}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </td>
                            <td className="py-3 px-3">
                              {rheaIdsList.length > 0 ? (
                                <div className="flex flex-col gap-1">
                                  {rheaIdsList.map((rheaId, rIdx) => (
                                    <a
                                      key={rIdx}
                                      href={getRheaUrl(rheaId)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="font-mono text-accent-amber hover:underline inline-flex items-center gap-1 text-xs"
                                    >
                                      {rheaId}
                                      <ExternalLink className="w-3 h-3" />
                                    </a>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-500">-</span>
                              )}
                            </td>
                            <td className="py-3 px-3 font-mono text-gray-300 text-xs">
                              {hit.score.toFixed(4)}
                            </td>
                            <td className="py-3 px-3 font-mono text-gray-400 text-xs">
                              {hit.bitscore?.toFixed(1) || '-'}
                            </td>
                            <td className="py-3 px-3 font-mono text-gray-400 text-xs">
                              {hit.full_seqid != null ? `${hit.full_seqid.toFixed(2)}%` : '-'}
                            </td>
                            <td className="py-3 px-3 font-mono text-gray-400 text-xs">
                              {hit.dmn_rmsd?.toFixed(2) || '-'}
                            </td>
                            <td className="py-3 px-3 font-mono text-gray-400 text-xs">
                              {hit.dmn_seqid != null ? `${hit.dmn_seqid.toFixed(2)}%` : '-'}
                            </td>
                            <td className="py-3 px-3 font-mono text-gray-400 text-xs">
                              {hit.dmn_tm?.toFixed(4) || '-'}
                            </td>
                          </tr>
                          {/* Reaction rows */}
                          {hasReactions && hit.reactions.map((equation: string, reactionIdx: number) => (
                            <tr
                              key={`reaction-${idx}-${reactionIdx}`}
                              className={`border-b border-white/5 ${rowClass}`}
                            >
                              <td colSpan={9} className="py-2 px-3">
                                <div className="text-gray-300 text-sm">
                                  {equation}
                                </div>
                                {hit.ec_numbers && hit.ec_numbers[reactionIdx] && hit.ec_numbers[reactionIdx] !== '' && (
                                  <div className="text-gray-400 text-xs mt-1">
                                    EC number:{' '}
                                    <a
                                      href={`https://enzyme.expasy.org/EC/${hit.ec_numbers[reactionIdx]}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-accent-cyan hover:underline"
                                    >
                                      {hit.ec_numbers[reactionIdx]}
                                    </a>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No FUJISAN hits found</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            {compounds.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-32">Ligand</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium">Name</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-36">Formula</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-24">PubChem</th>
                      <th className="text-center py-2 px-3 text-gray-400 font-medium w-36">Structure</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compounds.map((compound: CompoundInfo, idx: number) => (
                      <>
                        {/* Main row */}
                        <tr
                          key={`compound-${idx}`}
                          className={`border-b border-white/5 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`}
                        >
                          <td className="py-3 px-3">
                            <a
                              href={compound.chebi_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-mono text-accent-cyan hover:underline inline-flex items-center gap-1 text-xs"
                            >
                              {compound.chebi_id}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </td>
                          <td className="py-3 px-3 text-gray-300 max-w-xs">
                            {compound.name ? (
                              <span
                                className="block truncate"
                                title={compound.name.replace(/<[^>]*>/g, '')}
                                dangerouslySetInnerHTML={{ __html: compound.name }}
                              />
                            ) : (
                              <span className="text-gray-500">-</span>
                            )}
                          </td>
                          <td className="py-3 px-3 text-gray-300 font-mono text-xs">
                            <FormulaDisplay formula={compound.formula} />
                          </td>
                          <td className="py-3 px-3">
                            {compound.pubchem_cid ? (
                              <a
                                href={`https://pubchem.ncbi.nlm.nih.gov/compound/${compound.pubchem_cid}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-accent-green hover:underline inline-flex items-center gap-1 text-xs"
                              >
                                {compound.pubchem_cid}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            ) : (
                              <span className="text-gray-500">-</span>
                            )}
                          </td>
                          <td className="py-3 px-3 text-center" rowSpan={2}>
                            <a
                              href={compound.structure_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block"
                            >
                              <img
                                src={compound.structure_url}
                                alt={compound.name || compound.chebi_id}
                                className="w-28 h-28 object-contain bg-white rounded mx-auto"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none'
                                }}
                              />
                            </a>
                          </td>
                        </tr>
                        {/* SMILES row */}
                        <tr
                          key={`smiles-${idx}`}
                          className={`border-b border-white/10 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`}
                        >
                          <td colSpan={4} className="py-2 px-3">
                            {compound.smiles ? (
                              <div className="text-xs font-mono text-gray-500 break-all max-w-xl">
                                {compound.smiles}
                              </div>
                            ) : (
                              <span className="text-gray-500 text-xs">SMILES not available</span>
                            )}
                          </td>
                        </tr>
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No reaction participants found in FUJISAN hits</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
